import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Users } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { EmptyState } from '@/components/EmptyState';

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  title: string;
  department_name: string;
  employment_status: string;
  employment_type: string;
  hire_date: string;
}

export function EmployeesPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const navigate = useNavigate();

  const { data, loading } = useApi<{ employees: Employee[] }>(
    `employees?search=${search}&status=${status}`,
    [search, status],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search employees..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface-2 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>
        <select
          value={status}
          onChange={e => setStatus(e.target.value)}
          className="bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="on_leave">On Leave</option>
          <option value="terminated">Terminated</option>
        </select>
        <button
          onClick={() => navigate('/employees/new')}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Employee
        </button>
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading...</div>
      ) : (
        <DataTable
          columns={[
            { key: 'name', header: 'Name', render: (r: Employee) => <span className="font-medium">{r.first_name} {r.last_name}</span> },
            { key: 'email', header: 'Email' },
            { key: 'title', header: 'Title' },
            { key: 'department_name', header: 'Department' },
            { key: 'employment_type', header: 'Type', render: (r: Employee) => <span className="capitalize">{r.employment_type.replace('_', ' ')}</span> },
            { key: 'employment_status', header: 'Status', render: (r: Employee) => <StatusBadge status={r.employment_status} /> },
            { key: 'hire_date', header: 'Hire Date', render: (r: Employee) => r.hire_date?.split('T')[0] ?? '' },
          ]}
          data={data?.employees ?? []}
          onRowClick={(row) => navigate(`/employees/${row.id}`)}
          emptyNode={
            <EmptyState
              icon={<Users className="w-12 h-12" />}
              title="No employees yet"
              description="Add your first employee to get started."
              action={
                <button onClick={() => navigate('/employees/new')} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600">
                  <Plus className="w-4 h-4" /> Add Employee
                </button>
              }
            />
          }
        />
      )}
    </div>
  );
}
